import type { FetchResult } from "@apollo/client";
import { useMutation } from "@apollo/client";

import { ADD_GIT_ROOT } from "../../queries";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";
import { translate } from "utils/translations/translate";

const useAddGitRoot = (
  onUpdate: () => void,
  closeModal: () => void,
  setMessage: (messages: { message: string; type: string }) => void,
): {
  addGitRoot: (
    variables: Record<string, unknown>,
  ) => Promise<FetchResult<unknown>>;
} => {
  const [addGitRoot] = useMutation(ADD_GIT_ROOT, {
    onCompleted: (): void => {
      onUpdate();
      closeModal();
    },
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        switch (error.message) {
          case "Exception - Error empty value is not valid":
            setMessage({
              message: translate.t("group.scope.git.errors.invalid"),
              type: "error",
            });
            break;
          case "Exception - Root with the same nickname already exists":
            setMessage({
              message: translate.t(
                "group.scope.common.errors.duplicateNickname",
              ),
              type: "error",
            });
            break;
          case "Exception - Root with the same URL/branch already exists":
            setMessage({
              message: translate.t(
                "group.scope.common.errors.duplicateUrlBranch",
              ),
              type: "error",
            });
            break;
          case "Exception - Root name should not be included in the exception pattern":
            setMessage({
              message: translate.t("group.scope.git.errors.rootInGitignore"),
              type: "error",
            });
            break;
          case "Exception - Invalid characters":
            setMessage({
              message: translate.t("validations.invalidChar"),
              type: "error",
            });
            break;
          case "Exception - The URL is not valid":
            setMessage({
              message: translate.t("validations.invalidUrl"),
              type: "error",
            });
            break;
          case "Exception - Unsanitized input found":
            setMessage({
              message: translate.t("validations.unsanitizedInputFound"),
              type: "error",
            });
            break;
          case "Exception - A credential exists with the same name":
            setMessage({
              message: translate.t("validations.invalidCredentialName"),
              type: "error",
            });
            break;
          case "Exception - Git repository was not accessible with given credentials":
            setMessage({
              message: translate.t(
                "group.scope.git.errors.invalidGitCredentials",
              ),
              type: "error",
            });
            break;
          case "Exception - No commits found in the git repository being added":
            setMessage({
              message: translate.t("group.scope.git.errors.noCommitsFound"),
              type: "error",
            });
            break;
          case "Exception - Branch not found":
            setMessage({
              message: translate.t("group.scope.git.errors.invalidBranch"),
              type: "error",
            });
            break;
          case "Exception - Field cannot fill with blank characters":
            setMessage({
              message: translate.t("validations.invalidSpaceField"),
              type: "error",
            });
            break;
          case "Exception - Protocol http is invalid":
            setMessage({
              message: translate.t("validations.invalidHttpProtocol"),
              type: "error",
            });
            break;
          case "Exception - The action is not allowed during the free trial":
            msgError(translate.t("group.scope.git.errors.trial"));
            break;
          case "Exception - Git repository not found or access denied":
            setMessage({
              message: translate.t(
                "group.scope.git.errors.gitRepositoryNotFound",
              ),
              type: "error",
            });
            break;
          case "Exception - Git network connectivity issue":
            setMessage({
              message: translate.t("group.scope.git.errors.gitNetworkError"),
              type: "error",
            });
            break;
          case "Exception - Git server error":
            setMessage({
              message: translate.t("group.scope.git.errors.gitServerError"),
              type: "error",
            });
            break;
          case "Exception - Git client error":
            setMessage({
              message: translate.t("group.scope.git.errors.gitClientError"),
              type: "error",
            });
            break;
          default:
            setMessage({
              message: translate.t("groupAlerts.errorTextsad"),
              type: "error",
            });
            Logger.error("Couldn't add git roots", error);
        }
      });
    },
  });

  return { addGitRoot };
};

export { useAddGitRoot };
